import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CalendarClockIcon, InfoIcon, SearchXIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { ProvenanceBadge } from '../../components/trust/ProvenanceBadge';
import { programmes, scholarships, universityProfileById } from '../../data/admissions';
import { tuitionLabel } from '../../components/discover/CompareTray';

/**
 * One funding award.
 *
 * Eligibility leads, because the only question that matters is whether this is
 * worth your time. Campuzon does not award anything, and says so plainly rather
 * than letting a "Save" button imply otherwise.
 */
export function ScholarshipDetail() {
  const {
    scholarshipId = ''
  } = useParams();
  const navigate = useNavigate();
  const scholarship = scholarships.find(entry => entry.id === scholarshipId);
  if (!scholarship) {
    return <Panel data-id="element-11933" data-mp="30m3vmxg3dj6i">
        <EmptyState icon={<SearchXIcon className="h-5 w-5" data-id="element-11935" data-mp="3ee0ion2wl2w1" />} title="Award not found" description="This award is not published on Campuzon, or its record has been withdrawn." action={<Button variant="secondary" onClick={() => navigate('/discover')} data-id="element-11936" data-mp="2lpcuojqrrzes">
              Back to Discover
            </Button>} data-id="element-11934" data-mp="30mdd1wn2mi0l" />
      </Panel>;
  }
  const attached = programmes.filter(entry => entry.scholarshipIds.includes(scholarship.id));
  return <div className="space-y-6" data-id="element-11937" data-mp="2vlfv9l5z3pzm">
      <Link to="/discover" className="inline-flex items-center gap-1.5 text-body-sm font-medium text-ink-muted hover:text-ink" data-id="element-11938" data-mp="2qophx5ljz8hw">
        <ArrowLeftIcon aria-hidden className="h-4 w-4" data-id="element-11939" data-mp="avgpniceht06" />
        Discover
      </Link>

      <PageHeader eyebrow={`${scholarship.funder} · ${scholarship.level}`} title={scholarship.name} description={scholarship.coverage} actions={<ProvenanceBadge provenance={scholarship.provenance} data-id="element-11941" data-mp="hzq9va2f7w38" />} data-id="element-11940" data-mp="5xoy4pfo48wi" />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-11942" data-mp="mzl0dciaajnr">
        <div className="space-y-5" data-id="element-11943" data-mp="n0dwzknj3ss6">
          <Panel title="Who it is for" description="Every published condition. Meeting all of them is necessary, not sufficient." bodyClassName="p-0" data-id="element-11944" data-mp="ryfiec8fg46t">
            <ul className="divide-y divide-line" data-id="element-11945" data-mp="3vf4pq0fu3hxj">
              {scholarship.eligibility.map(item => <li key={item} className="px-5 py-3.5 text-body-sm text-ink sm:px-6" data-id="element-11946" data-mp="48l6bwvxmjnm">
                  {item}
                </li>)}
            </ul>
          </Panel>

          <Panel title="Programmes this is attached to" bodyClassName="p-0" data-id="element-11947" data-mp="37ptjw4jmmxcj">
            {attached.length === 0 ? <p className="px-5 py-4 text-body-sm text-ink-muted sm:px-6" data-id="element-11948" data-mp="3cod6m761od5y">
                Not linked to a specific programme on Campuzon. Read the funder's own terms for which
                courses qualify.
              </p> : <ul className="divide-y divide-line" data-id="element-11949" data-mp="3072fbwbuml70">
                {attached.map(programme => <li key={programme.id} className="px-5 py-4 sm:px-6" data-id="element-11950" data-mp="2v5d5gk0d4dki">
                    <Link to={`/discover/programmes/${programme.id}`} className="text-body font-semibold text-ink hover:underline" data-id="element-11951" data-mp="2q7bktunag66x">
                      {programme.title}
                    </Link>
                    <p className="cz-num mt-0.5 text-body-sm text-ink-muted" data-id="element-11952" data-mp="2cggjd3f4crdd">
                      {universityProfileById(programme.universityId)?.shortName} ·{' '}
                      {tuitionLabel(programme)} · {programme.deadlineLabel}
                    </p>
                  </li>)}
              </ul>}
          </Panel>

          <Panel title="What Campuzon does and does not do" data-id="element-11953" data-mp="1yhqn1q04ivq2">
            <ul className="space-y-2.5" data-id="element-11954" data-mp="2n7bge7m761lx">
              {['Campuzon publishes this record and dates it. It does not select, award or administer funding.', 'Nobody at Campuzon can influence a funding decision, and no provider on the marketplace can either.', 'Any request for payment in exchange for an award is a scam. Report it and we will investigate.', 'Saving this award adds it to your application workspace so requirements and deadlines appear together.'].map(line => <li key={line} className="flex items-start gap-2 text-body-sm text-ink" data-id="element-11955" data-mp="29dtxmp1c4swy">
                  <InfoIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-11956" data-mp="1vii4gm2tvai5" />
                  {line}
                </li>)}
            </ul>
          </Panel>
        </div>

        <div className="space-y-5" data-id="element-11957" data-mp="3pslp8zqnpw57">
          <Panel title="The award" data-id="element-11958" data-mp="3twjm13sieuv3">
            <p className="cz-num text-h3 font-semibold text-ink" data-id="element-11959" data-mp="3tvi36d59542t">{scholarship.amountLabel}</p>
            <p className="mt-1 text-body-sm text-ink-muted" data-id="element-11960" data-mp="1i97sg5nhj4uq">{scholarship.coverage}</p>
            <p className="mt-4 flex items-start gap-2 border-t border-line pt-4 text-body font-medium text-ink" data-id="element-11961" data-mp="1e84ghu8k5sys">
              <CalendarClockIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ink-subtle" data-id="element-11962" data-mp="1s02g9egdqcia" />
              {scholarship.deadlineLabel}
            </p>
          </Panel>

          <Panel title="Where this came from" data-id="element-11963" data-mp="21vvy9dcvay7g">
            <p className="max-w-[58ch] text-body-sm text-ink" data-id="element-11964" data-mp="21yryj8c6tyz2">{scholarship.sourceUrlLabel}</p>
            <p className="mt-2 max-w-[58ch] text-body-sm text-ink-muted" data-id="element-11965" data-mp="1wzotvp04durm">
              Always confirm against the funder's own page before you apply. If what you find there
              differs from this, tell us and we will correct the record.
            </p>
          </Panel>

          <Panel title="Add it to your applications" data-id="element-11966" data-mp="gjukbvvgdzfl">
            <p className="max-w-[58ch] text-body-sm text-ink" data-id="element-11967" data-mp="bkrfabfh3t7m">
              This puts the award beside the applications it could fund, so its deadline sits with
              theirs instead of in a separate list.
            </p>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-11968" data-mp="2gmdc4jta52t5">
              <Link to="/discover" data-id="element-11969" data-mp="2lkop33evftfl">
                <Button size="sm" variant="primary" data-id="element-11970" data-mp="2ljltwjain4tv">
                  Save to application
                </Button>
              </Link>
              <Link to="/services" data-id="element-11971" data-mp="2pk5trumh4mjp">
                <Button size="sm" variant="ghost" data-id="element-11972" data-mp="2ulv4ta9p507s">
                  Help with the application
                </Button>
              </Link>
            </div>
          </Panel>

          <Panel title="Level" data-id="element-11973" data-mp="2zi3iej88wlcu">
            <Badge plain data-id="element-11974" data-mp="34awj8w0lf9jn">{scholarship.level}</Badge>
          </Panel>
        </div>
      </div>
    </div>;
}